// File: src/router/index.js

import { createRouter, createWebHistory } from 'vue-router'
import { toRef } from 'vue'
import MainLayout from '@/layouts/MainLayout.vue'
import { useMainStore } from '@/stores/main'
import PreAuthLayout from '@/layouts/PreAuthLayout.vue'
import { useAuthz } from '@/composables/useAuthz'
import { ROLES } from '@/config/roles'
import { announce } from '@/utils/announce'

function createRoute(path, name, section, view = 'DefaultView', meta = {}, props = {}) {
    view = view || 'DefaultView'
    return {
        path,
        name,
        meta: { requiresAuth: meta.requiresAuth !== false, layout: MainLayout, ...meta },
        components: {
            default: () => import(`@/views/${section}/${view}.vue`)
        },
        props: { default: props }
    }
}

export const routes = [
    createRoute('/', 'Home', 'Dashboard', 'DashboardView'),
    createRoute('/dashboard', 'Dashboard', 'Dashboard', 'DashboardView'),
    createRoute('/my-account', 'My Account', 'Account', 'MyAccount'),
    createRoute('/about', 'About', 'About'),
    createRoute('/playground', 'Playground', 'Playground'),
    createRoute('/playground/old-look', 'PlaygroundOldLook', 'Playground', 'OldLookView'),

    // GlitchTip Error Tracking Test (Development only)
    ...(import.meta.env.DEV
        ? [
              {
                  path: '/glitchtip-test',
                  name: 'GlitchTipTest',
                  component: () => import('@/components/GlitchTipTest.vue'),
                  meta: { requiresAuth: false, layout: MainLayout }
              }
          ]
        : []),

    createRoute('/domains', 'Domains', 'Domains'),
    createRoute(
        '/interface-settings',
        'InterfaceSettings',
        'InterfaceSettings',
        'InterfaceSettingsView'
    ),
    createRoute('/support', 'Support', 'Support'),
    createRoute('/legal', 'Legal', 'Legal', 'LegalView'),
    createRoute('/email-protection', 'EmailProtection', 'EmailProtection'),
    createRoute('/email-reporting', 'EmailReporting', 'EmailReporting'),
    createRoute('/dmarc-reporting', 'DmarcReporting', 'DmarcReporting'),
    createRoute('/subscription', 'Subscriptions', 'Subscriptions', 'DefaultView'),
    createRoute('/help', 'Help', 'Help'),
    createRoute('/system-settings', 'SystemSettings', 'SystemSettings', 'SystemSettings'),
    createRoute('/help/:topicId', 'HelpTopic', 'Help', 'HelpTopicView'),
    createRoute('/feedback', 'Feedback', 'Feedback', 'FeedbackView'),
    createRoute('/product-changelog', 'Changelog', 'Admin', 'ChangelogView', {
        title: 'Product Changelog'
    }),
    createRoute(
        '/login',
        'Login',
        'Auth',
        'AuthView',
        { layout: PreAuthLayout, requiresAuth: false },
        { formType: 'login' }
    ),
    createRoute(
        '/activate',
        'AccountActivation',
        'Auth',
        'AuthView',
        { layout: PreAuthLayout, requiresAuth: false },
        { formType: 'activate' }
    ),
    createRoute(
        '/reset-password',
        'PasswordReset',
        'Auth',
        'AuthView',
        { layout: PreAuthLayout, requiresAuth: false },
        { formType: 'reset-password' }
    ),
    createRoute(
        '/set-password',
        'PasswordUpdate',
        'Auth',
        'AuthView',
        { layout: PreAuthLayout, requiresAuth: false },
        { formType: 'set-password' }
    ),
    createRoute(
        '/signup',
        'Signup',
        'Auth',
        'AuthView',
        { layout: PreAuthLayout, requiresAuth: false },
        { formType: 'signup' }
    ),
    // Organization Management - restricted to Platform Staff and MSP Tenant Admins
    {
        ...createRoute(
            '/customer-management',
            'CustomerManagement',
            'CustomerManagement',
            'DefaultView',
            {
                title: 'Customer Management',
                requiresRoles: [
                    ROLES.PLATFORM.ADMIN,
                    ROLES.PLATFORM.ENGINEER,
                    ROLES.PLATFORM.OPERATOR,
                    ROLES.PLATFORM.AUDITOR,
                    ROLES.PLATFORM.OBSERVER,
                    ROLES.TENANT.ADMIN
                ]
            }
        ),
        beforeEnter(to, from, next) {
            const mainStore = useMainStore()
            const claims = mainStore.claims || {}
            const roles = claims.roles || []

            // Platform staff always has access
            const isPlatformStaff = [
                ROLES.PLATFORM.ADMIN,
                ROLES.PLATFORM.ENGINEER,
                ROLES.PLATFORM.OPERATOR,
                ROLES.PLATFORM.AUDITOR,
                ROLES.PLATFORM.OBSERVER
            ].some((role) => roles.includes(role))

            if (isPlatformStaff) {
                next()
                return
            }

            // Tenant admins: only MSP admins can access (not SME admins)
            if (roles.includes(ROLES.TENANT.ADMIN)) {
                const orgType = mainStore.organization?.type
                if (orgType === 'msp') {
                    next()
                } else {
                    announce(
                        'Access denied: This page is only available to MSP administrators',
                        'error',
                        {
                            scope: 'ORGANIZATIONS'
                        }
                    )
                    next(from.path || '/')
                }
                return
            }

            // No valid role - deny access
            announce('Access denied: Insufficient permissions', 'error', {
                scope: 'ORGANIZATIONS'
            })
            next(from.path || '/')
        }
    },

    // Health-check route
    {
        path: '/health-check',
        name: 'HealthCheck',
        meta: { requiresAuth: false }, // No authentication required
        component: {
            template: '<div>OK</div>' // Inline component with a basic response
        }
    },

    // Catch-all route for 404 Not Found
    {
        path: '/:pathMatch(.*)*',
        name: 'NotFound',
        meta: { requiresAuth: false, layout: MainLayout },
        component: () => import('@/views/ErrorNotFound.vue')
    }
]

export const router = createRouter({
    history: createWebHistory(import.meta.env.BASE_URL),
    routes
})

// Function to handle navigation logic
export async function handleNavigation(to, from, next, mainStore) {
    const isAuthenticated = mainStore.isAuthenticated

    if (
        isAuthenticated &&
        (to.path === '/login' ||
            to.path.startsWith('/reset-password') ||
            to.path.startsWith('/set-password') ||
            to.path.startsWith('/signup'))
    ) {
        announce('You are already logged in.', 'info', { scope: 'AUTH' })
        next({ path: '/' })
    } else if (to.matched.some((record) => record.meta.requiresAuth) && !isAuthenticated) {
        next({ path: '/login' })
        announce('Please log in to access this page.', 'info', { scope: 'AUTH' })
    } else if (
        isAuthenticated &&
        to.meta.requiresPermission &&
        !mainStore.hasPermission(to.meta.requiresPermission)
    ) {
        announce('You do not have permission to view this page.', 'error', { scope: 'AUTH' })
        next({ path: '/' })
    } else if (isAuthenticated && to.meta.requiresRoles) {
        // Role-based access control using useAuthz composable
        const { hasAnyRole } = useAuthz(toRef(mainStore, 'claims'))

        if (hasAnyRole(...to.meta.requiresRoles)) {
            next()
        } else {
            announce('You do not have the required role to view this page.', 'error', {
                scope: 'AUTH'
            })
            next({ path: '/' })
        }
    } else {
        next()
    }
}

// Navigation Guard
router.beforeEach(async (to, from, next) => {
    const mainStore = useMainStore()
    const isAuthenticated = mainStore.isAuthenticated

    // Load user data if the user is authenticated and the data hasn't been loaded yet
    if (isAuthenticated && !mainStore.isUserDataLoaded) {
        try {
            await mainStore.loadUserData()
        } catch (error) {
            console.error('Failed to load user data:', error)
            // loadUserData() already announces the user-facing error and clears the session.
            // Keep the router focused on redirecting so the same failure only surfaces once.
            // Redirect to login explicitly so router.isReady() resolves cleanly.
            // next(false) would leave the initial navigation unresolved if the app
            // is still bootstrapping — causing the loading spinner to hang forever.
            next({ path: '/login' })
            return
        }
    }

    // Call handleNavigation after checking user data load
    await handleNavigation(to, from, next, mainStore)
})

export default router
